import React, { useState, useEffect } from 'react';
import {
	Box,
	Paper,
	Typography,
	CircularProgress,
	Grid,
	Card,
	CardContent,
	Chip,
	Divider,
	IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ThumbUp, ThumbDown } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { GetGuestSentimentAnalyses } from 'core/API/workplaces';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { IGuestSentiment } from '../interface/IGuestSentiment';

export default function GuestSentiment(): JSX.Element {
	const history = useHistory();
	const { workplaceId } = useWorkplacesContext();
	const [sentiment, setSentiment] = useState<IGuestSentiment | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchSentiment = async () => {
			setLoading(true);
			try {
				const response = await GetGuestSentimentAnalyses(workplaceId?.toString());
				if (response && response.length > 0) {
					// Get the most recent sentiment analysis
					const latestSentiment = response.sort((a: IGuestSentiment, b: IGuestSentiment) => 
						new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
					)[0];
					setSentiment(latestSentiment);
				}
			} catch (error) {
				console.error('Error fetching guest sentiment:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchSentiment();
	}, [workplaceId]);

	const getSentimentColor = (score: string) => {
		const numScore = parseFloat(score);
		if (numScore >= 8) return '#4caf50';
		if (numScore >= 6) return '#ff9800';
		return '#f44336';
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (!sentiment) {
		return (
			<Box sx={{ p: 3 }}>
				<Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
						<IconButton onClick={() => history.goBack()} sx={{ mr: 2 }}>
						<ArrowBackIcon />
					</IconButton>
					<Typography variant="h4">Guest Sentiment Analysis</Typography>
				</Box>
				<Paper sx={{ p: 4, textAlign: 'center' }}>
					<Typography>No sentiment data available for this workplace.</Typography>
				</Paper>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
			{/* Header */}
			<Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
					<IconButton onClick={() => history.goBack()} sx={{ mr: 2 }}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h4">Guest Sentiment Analysis</Typography>
			</Box>

			{/* Sentiment Score Card */}
			<Card sx={{ mb: 3, boxShadow: 3 }}>
				<CardContent>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12} md={4}>
							<Box sx={{ textAlign: 'center' }}>
								<Typography variant="h6" color="text.secondary" gutterBottom>
									Overall Sentiment Score
								</Typography>
								<Typography 
									variant="h1" 
									sx={{ 
										fontSize: '4rem', 
										fontWeight: 'bold',
										color: getSentimentColor(sentiment.sentimentScore)
									}}
								>
									{sentiment.sentimentScore}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									out of 10
								</Typography>
							</Box>
						</Grid>
						<Grid item xs={12} md={8}>
							<Typography variant="h6" gutterBottom>
								Workplace Information
							</Typography>
							<Typography variant="body1" color="text.secondary" gutterBottom>
								<strong>Location:</strong> {sentiment.workplaceName}
							</Typography>
							<Typography variant="body1" color="text.secondary" gutterBottom>
								<strong>Zipcode:</strong> {sentiment.zipcode}
							</Typography>
							<Typography variant="body1" color="text.secondary">
								<strong>Analysis Date:</strong> {new Date(sentiment.createdAt).toLocaleDateString()} at {new Date(sentiment.createdAt).toLocaleTimeString()}
							</Typography>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			{/* Summary */}
			{sentiment.summary && (
				<Card sx={{ mb: 3, boxShadow: 2 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Summary
						</Typography>
						<Typography variant="body1" color="text.secondary">
							{sentiment.summary}
						</Typography>
					</CardContent>
				</Card>
			)}

			<Grid container spacing={3}>
				{/* Top Likes */}
				<Grid item xs={12} md={6}>
					<Card sx={{ height: '100%', boxShadow: 2 }}>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
								<ThumbUp sx={{ mr: 1, color: '#4caf50' }} />
								<Typography variant="h6">
									Top Likes
								</Typography>
							</Box>
							<Divider sx={{ mb: 2 }} />
							{sentiment.topLikes && sentiment.topLikes.length > 0 ? (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
									{sentiment.topLikes.map((like, index) => (
										<Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
											<Chip 
												label={`${index + 1}`}
												size="small"
												sx={{ 
													mr: 2, 
													bgcolor: '#4caf50', 
													color: 'white',
													fontWeight: 'bold',
													minWidth: '32px'
												}} 
											/>
											<Typography variant="body1">{like}</Typography>
										</Box>
									))}
								</Box>
							) : (
								<Typography color="text.secondary">No likes data available</Typography>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Top Dislikes */}
				<Grid item xs={12} md={6}>
					<Card sx={{ height: '100%', boxShadow: 2 }}>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
								<ThumbDown sx={{ mr: 1, color: '#f44336' }} />
								<Typography variant="h6">
									Top Dislikes
								</Typography>
							</Box>
							<Divider sx={{ mb: 2 }} />
							{sentiment.topDislikes && sentiment.topDislikes.length > 0 ? (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
									{sentiment.topDislikes.map((dislike, index) => (
										<Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
											<Chip 
												label={`${index + 1}`}
												size="small"
												sx={{ 
													mr: 2, 
													bgcolor: '#f44336', 
													color: 'white',
													fontWeight: 'bold',
													minWidth: '32px'
												}} 
											/>
											<Typography variant="body1">{dislike}</Typography>
										</Box>
									))}
								</Box>
							) : (
								<Typography color="text.secondary">No dislikes data available</Typography>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
}
